import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Drawer,
  IconButton,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import LocalOfferRoundedIcon from "@mui/icons-material/LocalOfferRounded";
import WhatshotRoundedIcon from "@mui/icons-material/WhatshotRounded";
import AssignmentIndRoundedIcon from "@mui/icons-material/AssignmentIndRounded";
import NotificationsActiveRoundedIcon from "@mui/icons-material/NotificationsActiveRounded";
import TrendingUpRoundedIcon from "@mui/icons-material/TrendingUpRounded";
import InsightsRoundedIcon from "@mui/icons-material/InsightsRounded";
import { alpha } from "@mui/material/styles";
import { omnichannelInboxApi } from "../../services/omnichannelInboxApi";

const TEXT = "#183247";
const SUBTEXT = "#617488";
const ACCENT = "#D98B1B";

function SummaryTile({ label, value, icon }) {
  return (
    <Paper
      elevation={0}
      sx={{
        px: 1.4,
        py: 1.2,
        borderRadius: 3,
        border: "1px solid #E8EDF1",
        bgcolor: "#FFFFFF",
        flex: 1,
        minWidth: 135,
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center">
        <Box
          sx={{
            width: 34,
            height: 34,
            borderRadius: "50%",
            bgcolor: alpha(ACCENT, 0.12),
            color: ACCENT,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          {icon}
        </Box>
        <Box>
          <Typography variant="caption" sx={{ color: SUBTEXT, fontWeight: 800, textTransform: "uppercase" }}>
            {label}
          </Typography>
          <Typography variant="h6" sx={{ color: TEXT, fontWeight: 800, lineHeight: 1.1 }}>
            {value}
          </Typography>
        </Box>
      </Stack>
    </Paper>
  );
}

const attentionTone = (level) => {
  switch (String(level || "").toLowerCase()) {
    case "critical":
      return { bg: "rgba(216, 78, 78, 0.12)", color: "#C43E3E" };
    case "high":
      return { bg: "rgba(217, 139, 27, 0.14)", color: "#A76400" };
    case "watch":
      return { bg: "rgba(46, 119, 208, 0.12)", color: "#225EA8" };
    default:
      return { bg: "#FFFFFF", color: SUBTEXT };
  }
};

const managerAlertTone = (level) => {
  switch (String(level || "").toLowerCase()) {
    case "critical":
      return { bg: "rgba(216, 78, 78, 0.12)", color: "#C43E3E" };
    case "high":
      return { bg: "rgba(217, 139, 27, 0.14)", color: "#A76400" };
    case "watch":
      return { bg: "rgba(46, 119, 208, 0.12)", color: "#225EA8" };
    default:
      return { bg: "#F5F8FB", color: TEXT };
  }
};

const formatPercent = (value) => `${Math.round(Number(value || 0))}%`;

const formatAmount = (value) => {
  const amount = Number(value || 0);
  return `USD ${amount.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: amount >= 1000 ? 0 : 2,
  })}`;
};

const formatDateTime = (value) => {
  if (!value) {
    return "";
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return "";
  }

  return parsed.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const isTerminalQuote = (status) =>
  ["won", "lost", "expired", "merged"].includes(String(status || "").toLowerCase());

export default function QuoteOpportunitiesDrawer({ open, onClose, onSelectConversation, scope = {} }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [overview, setOverview] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const [actionQuoteId, setActionQuoteId] = useState(null);
  const [actionCode, setActionCode] = useState("");
  const channel = scope?.channel || "";
  const whatsappLineScope = scope?.whatsappLineScope || "";
  const whatsappLineId = scope?.whatsappLineId || "";
  const workstreamId = scope?.workstreamId || "";
  const managerScoringUi = Boolean(overview?.ui_flags?.manager_scoring_ui);
  const managerAnalyticsUi = Boolean(overview?.ui_flags?.manager_analytics_ui);
  const managerAutomationUi = Boolean(overview?.ui_flags?.manager_automation_ui);
  const managerInterventionUi = Boolean(overview?.ui_flags?.manager_intervention_ui);

  const loadDrawerData = useCallback(async () => {
    const dashboardScope = {
      channel: channel || undefined,
      whatsapp_line_scope: whatsappLineScope || undefined,
      whatsapp_line_id: whatsappLineId || undefined,
      workstream_id: workstreamId || undefined,
    };

    const [overviewResponse, queueResponse] = await Promise.all([
      omnichannelInboxApi.getQuoteOverview(dashboardScope),
      omnichannelInboxApi.listQuoteQueue({
        ...dashboardScope,
        limit: 50,
      }),
    ]);

    const rawQuotes = Array.isArray(queueResponse?.data) ? queueResponse.data : [];
    const uniqueQuotes = Array.from(
      new Map(rawQuotes.map((quote) => [quote?.id, quote])).values()
    );

    return {
      overview: overviewResponse?.data || null,
      quotes: uniqueQuotes,
    };
  }, [channel, whatsappLineId, whatsappLineScope, workstreamId]);

  useEffect(() => {
    if (!open) return undefined;

    let cancelled = false;
    setLoading(true);
    setError("");

    loadDrawerData()
      .then((payload) => {
        if (cancelled) return;
        setOverview(payload.overview);
        setQuotes(payload.quotes);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.response?.data?.message || err?.message || "Failed to load quote opportunities.");
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [open, loadDrawerData]);

  const refreshDrawerData = useCallback(async () => {
    const payload = await loadDrawerData();
    setOverview(payload.overview);
    setQuotes(payload.quotes);
  }, [loadDrawerData]);

  const handleInterventionAction = useCallback(
    async (event, quoteId, type) => {
      event.stopPropagation();
      setActionQuoteId(quoteId);
      setActionCode(type);
      setError("");

      try {
        await omnichannelInboxApi.startQuoteIntervention(quoteId, { type });
        await refreshDrawerData();
      } catch (err) {
        setError(err?.response?.data?.message || err?.message || "Failed to start manager intervention.");
      } finally {
        setActionQuoteId(null);
        setActionCode("");
      }
    },
    [refreshDrawerData]
  );

  const handleResolveIntervention = useCallback(
    async (event, quoteId) => {
      event.stopPropagation();
      setActionQuoteId(quoteId);
      setActionCode("resolve");
      setError("");

      try {
        await omnichannelInboxApi.resolveQuoteIntervention(quoteId, { resolution_code: "resolved" });
        await refreshDrawerData();
      } catch (err) {
        setError(err?.response?.data?.message || err?.message || "Failed to resolve manager intervention.");
      } finally {
        setActionQuoteId(null);
        setActionCode("");
      }
    },
    [refreshDrawerData]
  );

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 430 },
          bgcolor: "#F8FAFC",
          borderLeft: "1px solid #DCE4EA",
        },
      }}
    >
      <Box sx={{ p: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
          <Box>
            <Typography variant="h6" sx={{ color: TEXT, fontWeight: 800 }}>
              Quote Opportunities
            </Typography>
            <Typography variant="body2" sx={{ color: SUBTEXT }}>
              Track hot quotes, overdue follow-ups, and conversion signals from one place.
            </Typography>
          </Box>
          <IconButton onClick={onClose} sx={{ width: 36, height: 36 }}>
            <CloseRoundedIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Stack>

        {error ? <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert> : null}

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress size={30} />
          </Box>
        ) : (
          <Stack spacing={1.4} sx={{ mt: 2 }}>
            <Stack direction="row" spacing={1.1} useFlexGap sx={{ flexWrap: "wrap" }}>
              <SummaryTile label="Detected Today" value={overview?.detected_today || 0} icon={<LocalOfferRoundedIcon sx={{ fontSize: 18 }} />} />
              <SummaryTile label="Follow-up Due" value={overview?.follow_up_due || 0} icon={<NotificationsActiveRoundedIcon sx={{ fontSize: 18 }} />} />
              <SummaryTile label="At Risk" value={overview?.at_risk || 0} icon={<NotificationsActiveRoundedIcon sx={{ fontSize: 18 }} />} />
              <SummaryTile label="Hot At Risk" value={overview?.hot_at_risk || 0} icon={<WhatshotRoundedIcon sx={{ fontSize: 18 }} />} />
              <SummaryTile label="Unassigned" value={overview?.unassigned || 0} icon={<AssignmentIndRoundedIcon sx={{ fontSize: 18 }} />} />
            </Stack>

            {managerScoringUi ? (
              <>
                <Stack direction="row" spacing={1.1} useFlexGap sx={{ flexWrap: "wrap" }}>
                  <SummaryTile label="Health Score" value={overview?.health_score ?? 100} icon={<WhatshotRoundedIcon sx={{ fontSize: 18 }} />} />
                  <SummaryTile label="Needs Attention" value={overview?.manager_attention_required || 0} icon={<NotificationsActiveRoundedIcon sx={{ fontSize: 18 }} />} />
                  <SummaryTile label="High-Intent" value={overview?.high_intent_leads || 0} icon={<LocalOfferRoundedIcon sx={{ fontSize: 18 }} />} />
                  <SummaryTile label="Price Sensitive" value={overview?.price_sensitive_leads || 0} icon={<AssignmentIndRoundedIcon sx={{ fontSize: 18 }} />} />
                  <SummaryTile label="Multi-channel" value={overview?.multi_channel_leads || 0} icon={<AssignmentIndRoundedIcon sx={{ fontSize: 18 }} />} />
                  <SummaryTile label="Re-engaged" value={overview?.reengaged_customers || 0} icon={<NotificationsActiveRoundedIcon sx={{ fontSize: 18 }} />} />
                </Stack>

                {(overview?.attention_breakdown || []).length ? (
                  <Paper
                    elevation={0}
                    sx={{
                      px: 1.5,
                      py: 1.35,
                      borderRadius: 3,
                      border: "1px solid #E6ECF1",
                      bgcolor: "#FFFFFF",
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ color: TEXT, fontWeight: 800 }}>
                      Manager Focus Areas
                    </Typography>
                    <Stack direction="row" spacing={0.8} useFlexGap flexWrap="wrap" sx={{ mt: 1 }}>
                      {(overview?.attention_breakdown || []).map((item) => (
                        <Chip
                          key={item.code}
                          size="small"
                          label={`${item.label}: ${item.count}`}
                          sx={{ bgcolor: "#FFF8EA", color: "#A76400", fontWeight: 700 }}
                        />
                      ))}
                    </Stack>
                  </Paper>
                ) : null}
              </>
            ) : null}

            {managerAnalyticsUi ? (
              <>
                <Stack direction="row" spacing={1.1} useFlexGap sx={{ flexWrap: "wrap" }}>
                  <SummaryTile label="Conversion Rate" value={formatPercent(overview?.conversion_rate)} icon={<TrendingUpRoundedIcon sx={{ fontSize: 18 }} />} />
                  <SummaryTile label="Recent Win Rate" value={formatPercent(overview?.recent_conversion_rate)} icon={<InsightsRoundedIcon sx={{ fontSize: 18 }} />} />
                  <SummaryTile label="Weighted Pipeline" value={formatAmount(overview?.weighted_pipeline_value)} icon={<LocalOfferRoundedIcon sx={{ fontSize: 18 }} />} />
                  <SummaryTile
                    label="Projected Wins"
                    value={Number(overview?.projected_wins || 0).toLocaleString(undefined, { maximumFractionDigits: 1 })}
                    icon={<WhatshotRoundedIcon sx={{ fontSize: 18 }} />}
                  />
                </Stack>

                {(overview?.owner_conversion_leaderboard || []).length ? (
                  <Paper
                    elevation={0}
                    sx={{
                      px: 1.5,
                      py: 1.35,
                      borderRadius: 3,
                      border: "1px solid #E6ECF1",
                      bgcolor: "#FFFFFF",
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ color: TEXT, fontWeight: 800 }}>
                      Owner Conversion
                    </Typography>
                    <Stack spacing={1} sx={{ mt: 1.1 }}>
                      {(overview?.owner_conversion_leaderboard || []).slice(0, 4).map((item) => (
                        <Stack
                          key={item.owner_id}
                          direction="row"
                          justifyContent="space-between"
                          alignItems="center"
                          spacing={1}
                        >
                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="body2" sx={{ color: TEXT, fontWeight: 700 }}>
                              {item.owner_name || `Owner #${item.owner_id}`}
                            </Typography>
                            <Typography variant="caption" sx={{ color: SUBTEXT }}>
                              {item.won || 0} won | {item.closed || 0} closed | {formatAmount(item.weighted_pipeline_value || 0)} weighted
                            </Typography>
                          </Box>
                          <Chip
                            size="small"
                            label={formatPercent(item.conversion_rate)}
                            sx={{ bgcolor: "#EEF6FF", color: "#225EA8", fontWeight: 700 }}
                          />
                        </Stack>
                      ))}
                    </Stack>
                  </Paper>
                ) : null}

                {(overview?.top_service_types || []).length || (overview?.top_routes || []).length ? (
                  <Paper
                    elevation={0}
                    sx={{
                      px: 1.5,
                      py: 1.35,
                      borderRadius: 3,
                      border: "1px solid #E6ECF1",
                      bgcolor: "#FFFFFF",
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ color: TEXT, fontWeight: 800 }}>
                      Quote Trends
                    </Typography>

                    {(overview?.top_service_types || []).length ? (
                      <>
                        <Typography variant="caption" sx={{ color: SUBTEXT, fontWeight: 700, display: "block", mt: 1 }}>
                          Top services
                        </Typography>
                        <Stack direction="row" spacing={0.8} useFlexGap flexWrap="wrap" sx={{ mt: 0.8 }}>
                          {(overview?.top_service_types || []).map((item) => (
                            <Chip
                              key={item.code}
                              size="small"
                              label={`${item.label}: ${item.count}`}
                              sx={{ bgcolor: "#F5F8FB", color: TEXT, fontWeight: 700 }}
                            />
                          ))}
                        </Stack>
                      </>
                    ) : null}

                    {(overview?.top_routes || []).length ? (
                      <>
                        <Typography variant="caption" sx={{ color: SUBTEXT, fontWeight: 700, display: "block", mt: 1.2 }}>
                          Top routes
                        </Typography>
                        <Stack direction="row" spacing={0.8} useFlexGap flexWrap="wrap" sx={{ mt: 0.8 }}>
                          {(overview?.top_routes || []).map((item) => (
                            <Chip
                              key={item.label}
                              size="small"
                              label={`${item.label}: ${item.count}`}
                              sx={{ bgcolor: "#FFF8EA", color: "#A76400", fontWeight: 700 }}
                            />
                          ))}
                        </Stack>
                      </>
                    ) : null}
                  </Paper>
                ) : null}

                {(overview?.lost_reason_breakdown || []).length ? (
                  <Paper
                    elevation={0}
                    sx={{
                      px: 1.5,
                      py: 1.35,
                      borderRadius: 3,
                      border: "1px solid #E6ECF1",
                      bgcolor: "#FFFFFF",
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ color: TEXT, fontWeight: 800 }}>
                      Loss Signals
                    </Typography>
                    <Stack direction="row" spacing={0.8} useFlexGap flexWrap="wrap" sx={{ mt: 1 }}>
                      {(overview?.lost_reason_breakdown || []).map((item) => (
                        <Chip
                          key={item.code}
                          size="small"
                          label={`${item.label}: ${item.count}`}
                          sx={{ bgcolor: "rgba(216, 78, 78, 0.10)", color: "#C43E3E", fontWeight: 700 }}
                        />
                      ))}
                    </Stack>
                  </Paper>
                ) : null}
              </>
            ) : null}

            {managerAutomationUi ? (
              <>
                <Stack direction="row" spacing={1.1} useFlexGap sx={{ flexWrap: "wrap" }}>
                  <SummaryTile label="Save Deal" value={overview?.save_deal_now || 0} icon={<NotificationsActiveRoundedIcon sx={{ fontSize: 18 }} />} />
                  <SummaryTile label="Forecast Risk" value={overview?.forecast_risk_quotes || 0} icon={<TrendingUpRoundedIcon sx={{ fontSize: 18 }} />} />
                  <SummaryTile label="Owner Watch" value={overview?.owner_recovery_watch_count || 0} icon={<AssignmentIndRoundedIcon sx={{ fontSize: 18 }} />} />
                </Stack>

                {(overview?.automation_watchlist || []).length ? (
                  <Paper
                    elevation={0}
                    sx={{
                      px: 1.5,
                      py: 1.35,
                      borderRadius: 3,
                      border: "1px solid #E6ECF1",
                      bgcolor: "#FFFFFF",
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ color: TEXT, fontWeight: 800 }}>
                      Automation Watchlist
                    </Typography>
                    <Stack spacing={1} sx={{ mt: 1.1 }}>
                      {(overview?.automation_watchlist || []).map((item) => (
                        <Stack key={item.quote_id} spacing={0.4}>
                          <Stack direction="row" justifyContent="space-between" spacing={1}>
                            <Typography variant="body2" sx={{ color: TEXT, fontWeight: 700 }}>
                              {item.customer_name}
                            </Typography>
                            <Chip
                              size="small"
                              label={item.label}
                              sx={{
                                bgcolor: managerAlertTone(item.level).bg,
                                color: managerAlertTone(item.level).color,
                                fontWeight: 700,
                              }}
                            />
                          </Stack>
                          <Typography variant="caption" sx={{ color: SUBTEXT }}>
                            Priority {item.priority_score || 0} | Forecast {formatPercent(item.forecast_probability)} | {formatAmount(item.weighted_value || 0)} weighted
                          </Typography>
                        </Stack>
                      ))}
                    </Stack>
                  </Paper>
                ) : null}

                {(overview?.owner_recovery_watch || []).length ? (
                  <Paper
                    elevation={0}
                    sx={{
                      px: 1.5,
                      py: 1.35,
                      borderRadius: 3,
                      border: "1px solid #E6ECF1",
                      bgcolor: "#FFFFFF",
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ color: TEXT, fontWeight: 800 }}>
                      Owner Recovery Watch
                    </Typography>
                    <Stack spacing={0.8} sx={{ mt: 1 }}>
                      {(overview?.owner_recovery_watch || []).slice(0, 4).map((item) => (
                        <Stack key={item.owner_id} direction="row" justifyContent="space-between" spacing={1}>
                          <Typography variant="body2" sx={{ color: TEXT, fontWeight: 700 }}>
                            {item.owner_name || `Owner #${item.owner_id}`}
                          </Typography>
                          <Typography variant="caption" sx={{ color: SUBTEXT }}>
                            {formatPercent(item.conversion_rate)} conversion | {item.closed || 0} closed
                          </Typography>
                        </Stack>
                      ))}
                    </Stack>
                  </Paper>
                ) : null}
              </>
            ) : null}

            {managerInterventionUi ? (
              <>
                <Stack direction="row" spacing={1.1} useFlexGap sx={{ flexWrap: "wrap" }}>
                  <SummaryTile label="Active Interventions" value={overview?.active_interventions || 0} icon={<NotificationsActiveRoundedIcon sx={{ fontSize: 18 }} />} />
                  <SummaryTile label="Takeovers" value={overview?.takeover_interventions || 0} icon={<AssignmentIndRoundedIcon sx={{ fontSize: 18 }} />} />
                  <SummaryTile label="Manager Watch" value={overview?.watch_interventions || 0} icon={<InsightsRoundedIcon sx={{ fontSize: 18 }} />} />
                  <SummaryTile label="Coaching" value={overview?.coaching_interventions || 0} icon={<TrendingUpRoundedIcon sx={{ fontSize: 18 }} />} />
                </Stack>

                {(overview?.intervention_watchlist || []).length ? (
                  <Paper
                    elevation={0}
                    sx={{
                      px: 1.5,
                      py: 1.35,
                      borderRadius: 3,
                      border: "1px solid #E6ECF1",
                      bgcolor: "#FFFFFF",
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ color: TEXT, fontWeight: 800 }}>
                      Interventions In Progress
                    </Typography>
                    <Stack spacing={1} sx={{ mt: 1.1 }}>
                      {(overview?.intervention_watchlist || []).map((item) => (
                        <Stack key={item.quote_id} spacing={0.35}>
                          <Stack direction="row" justifyContent="space-between" spacing={1}>
                            <Typography variant="body2" sx={{ color: TEXT, fontWeight: 700 }}>
                              {item.customer_name}
                            </Typography>
                            <Chip
                              size="small"
                              label={item.label}
                              sx={{ bgcolor: "#FFF3E0", color: "#A76400", fontWeight: 700 }}
                            />
                          </Stack>
                          <Typography variant="caption" sx={{ color: SUBTEXT }}>
                            {item.started_by_name ? `${item.started_by_name} started it` : "Manager started it"}
                            {item.started_at ? ` • ${formatDateTime(item.started_at)}` : ""}
                          </Typography>
                          {item.note ? (
                            <Typography variant="caption" sx={{ color: SUBTEXT }}>
                              {item.note}
                            </Typography>
                          ) : null}
                        </Stack>
                      ))}
                    </Stack>
                  </Paper>
                ) : null}
              </>
            ) : null}

            <Stack spacing={1}>
              {quotes.length ? (
                quotes.map((quote) => (
                  <Paper
                    key={quote.id}
                    elevation={0}
                    onClick={() => {
                      if (quote.primary_conversation_id) {
                        onSelectConversation?.(quote.primary_conversation_id);
                      }
                      onClose?.();
                    }}
                    sx={{
                      px: 1.5,
                      py: 1.3,
                      borderRadius: 3,
                      border: "1px solid #E6ECF1",
                      bgcolor: "#FFFFFF",
                      cursor: "pointer",
                      "&:hover": {
                        bgcolor: "#FFF9EE",
                      },
                    }}
                  >
                    <Stack spacing={0.75}>
                      <Stack direction="row" justifyContent="space-between" spacing={1}>
                        <Typography variant="subtitle2" sx={{ color: TEXT, fontWeight: 800 }}>
                          {quote.customer_name || quote.customer_email || quote.customer_phone || `Quote #${quote.id}`}
                        </Typography>
                        <Typography variant="caption" sx={{ color: quote.hotness_band === "hot" ? "#C43E3E" : SUBTEXT, fontWeight: 800 }}>
                          {(quote.hotness_band || "cold").toUpperCase()}
                        </Typography>
                      </Stack>

                      {managerScoringUi ? (
                        <Stack direction="row" spacing={0.8} useFlexGap flexWrap="wrap">
                          <Chip
                            size="small"
                            label={`Attention ${quote.attention_score || 0}`}
                            sx={{
                              bgcolor: attentionTone(quote.attention_level).bg,
                              color: attentionTone(quote.attention_level).color,
                              fontWeight: 700,
                            }}
                          />
                          {quote.focus_reason ? (
                            <Chip
                              size="small"
                              label={quote.focus_reason}
                              sx={{ bgcolor: "#FFFFFF", color: TEXT, border: "1px solid #DCE4EA", fontWeight: 700 }}
                            />
                          ) : null}
                          {managerAnalyticsUi ? (
                            <Chip
                              size="small"
                              label={`Forecast ${formatPercent(quote.forecast_probability)}`}
                              sx={{ bgcolor: "#EEF6FF", color: "#225EA8", fontWeight: 700 }}
                            />
                          ) : null}
                          {managerAutomationUi && quote.manager_alert_label ? (
                            <Chip
                              size="small"
                              label={quote.manager_alert_label}
                              sx={{
                                bgcolor: managerAlertTone(quote.manager_alert_level).bg,
                                color: managerAlertTone(quote.manager_alert_level).color,
                                fontWeight: 700,
                              }}
                            />
                          ) : null}
                          {managerInterventionUi && quote.manager_intervention?.active ? (
                            <Chip
                              size="small"
                              label={quote.manager_intervention?.label || "Manager intervention"}
                              sx={{ bgcolor: "#FFF3E0", color: "#A76400", fontWeight: 700 }}
                            />
                          ) : null}
                        </Stack>
                      ) : null}

                      <Typography variant="body2" sx={{ color: SUBTEXT }}>
                        {quote.route_label || "Route pending"} {quote.travel_date_text ? `- ${quote.travel_date_text}` : ""}
                      </Typography>

                      <Stack direction="row" justifyContent="space-between" spacing={1}>
                        <Typography variant="body2" sx={{ color: TEXT, fontWeight: 700 }}>
                          {quote.status_label || "Quote"}
                        </Typography>
                        <Typography variant="body2" sx={{ color: SUBTEXT }}>
                          {quote.owner?.name || "Unassigned"}
                        </Typography>
                      </Stack>

                      {managerScoringUi && quote.manager_reasons?.length ? (
                        <Typography variant="caption" sx={{ color: SUBTEXT, lineHeight: 1.5 }}>
                          {quote.manager_reasons.slice(0, 2).join(" | ")}
                        </Typography>
                      ) : null}

                      {managerAutomationUi && quote.manager_playbook_steps?.length ? (
                        <Typography variant="caption" sx={{ color: SUBTEXT, lineHeight: 1.5 }}>
                          {quote.manager_playbook_steps[0]}
                        </Typography>
                      ) : null}

                      {managerInterventionUi && quote.manager_intervention?.active ? (
                        <Typography variant="caption" sx={{ color: SUBTEXT, lineHeight: 1.5 }}>
                          {(quote.manager_intervention?.started_by_name
                            ? `${quote.manager_intervention.label} by ${quote.manager_intervention.started_by_name}`
                            : quote.manager_intervention?.label || "Manager intervention")}
                          {quote.manager_intervention?.started_at ? ` • ${formatDateTime(quote.manager_intervention.started_at)}` : ""}
                          {quote.manager_intervention?.note ? ` • ${quote.manager_intervention.note}` : ""}
                        </Typography>
                      ) : null}

                      {managerInterventionUi ? (
                        <Stack
                          direction="row"
                          spacing={0.8}
                          useFlexGap
                          flexWrap="wrap"
                          onClick={(event) => event.stopPropagation()}
                        >
                          {quote.manager_intervention?.active ? (
                            <Button
                              size="small"
                              variant="outlined"
                              disabled={actionQuoteId === quote.id}
                              onClick={(event) => handleResolveIntervention(event, quote.id)}
                              sx={{ borderRadius: 999, textTransform: "none", fontWeight: 700 }}
                            >
                              {actionQuoteId === quote.id && actionCode === "resolve" ? "Resolving..." : "Resolve"}
                            </Button>
                          ) : !isTerminalQuote(quote.status) ? (
                            <>
                              <Button
                                size="small"
                                variant="outlined"
                                disabled={actionQuoteId === quote.id}
                                onClick={(event) => handleInterventionAction(event, quote.id, "watch")}
                                sx={{ borderRadius: 999, textTransform: "none", fontWeight: 700 }}
                              >
                                {actionQuoteId === quote.id && actionCode === "watch" ? "Starting..." : "Watch"}
                              </Button>
                              <Button
                                size="small"
                                variant="outlined"
                                disabled={actionQuoteId === quote.id}
                                onClick={(event) => handleInterventionAction(event, quote.id, "coach_owner")}
                                sx={{ borderRadius: 999, textTransform: "none", fontWeight: 700 }}
                              >
                                {actionQuoteId === quote.id && actionCode === "coach_owner" ? "Starting..." : "Coach"}
                              </Button>
                              <Button
                                size="small"
                                variant="contained"
                                disabled={actionQuoteId === quote.id}
                                onClick={(event) => handleInterventionAction(event, quote.id, "take_over")}
                                sx={{
                                  borderRadius: 999,
                                  textTransform: "none",
                                  fontWeight: 700,
                                  boxShadow: "none",
                                  bgcolor: "#1FB84F",
                                  "&:hover": { bgcolor: "#15933D", boxShadow: "none" },
                                }}
                              >
                                {actionQuoteId === quote.id && actionCode === "take_over" ? "Starting..." : "Take over"}
                              </Button>
                            </>
                          ) : null}
                        </Stack>
                      ) : null}
                    </Stack>
                  </Paper>
                ))
              ) : (
                <Alert severity="info">No quote opportunities have been detected yet.</Alert>
              )}
            </Stack>
          </Stack>
        )}
      </Box>
    </Drawer>
  );
}
